import { useMemo } from 'react';
import {
  useLocation, useNavigate 
} from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  TABS, type TabId 
} from './sourcing-constants';
import { EventsView } from './events-view';
import { CreateEventView } from './create-view';

export function SourcingPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = useMemo((): TabId => {
    const path = location.pathname;
    if (path === '/sourcing/auctions') return 'auctions';
    if (path === '/sourcing/proposals') return 'proposals';
    if (path === '/sourcing/create') return 'create';
    return 'all';
  }, [location.pathname]);

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Sourcing Events</h1>
          <p className="text-muted-foreground">Manage reverse auctions and sealed proposals</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="border-b">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.href)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'all' && (
        <EventsView
          title="All Sourcing Events"
          description="View and manage all reverse auctions and sealed proposals"
        />
      )}
      {activeTab === 'auctions' && (
        <EventsView
          filterType="reverse_auction"
          title="Reverse Auctions"
          description="Live bidding events where suppliers compete for the lowest price"
        />
      )}
      {activeTab === 'proposals' && (
        <EventsView
          filterType="sealed_proposal"
          title="Sealed Proposals"
          description="Hidden bid events evaluated after the deadline"
        />
      )}
      {activeTab === 'create' && <CreateEventView />}
    </div>
  );
}
